const START_GUARD_MARKER = Symbol.for('membergy.livewire.start.guard')
const ORIGINAL_START_MARKER = Symbol.for('membergy.livewire.start.guard.original')

const warnAboutTelemetryFailure = (error) => {
  try {
    console.warn('[MEMBERGY-B5] Duplicate-start telemetry failed', error)
  } catch {
    // Diagnostics must never turn a suppressed duplicate start into an error.
  }
}

/**
 * Make the shared Livewire export's start method idempotent.
 *
 * Livewire's DOMContentLoaded callback reads `start` from the same exported
 * object later, so replacing the method protects both automatic and explicit
 * starts in the observed production path. The global symbol lets a re-evaluated
 * guard recognize the wrapper on that same export object and therefore retain
 * its closure state. It does not coordinate independent Livewire/Alpine bundle
 * instances. A separate non-enumerable symbol retains the original method for
 * the real-bundle regression's negative control. No guard state is placed in
 * Alpine reactivity.
 *
 * @param {{ start: (...args: unknown[]) => unknown }} Livewire
 * @param {{ onDuplicateStart?: () => void }} [options]
 * @returns {(...args: unknown[]) => unknown}
 */
export default function guardLivewireStart(Livewire, { onDuplicateStart } = {}) {
  const start = Livewire?.start

  if (typeof start !== 'function') {
    throw new TypeError('Livewire.start must be a function')
  }

  if (start[START_GUARD_MARKER] === true) {
    return start
  }

  let started = false
  let duplicateReported = false

  const guardedStart = function(...args) {
    if (started) {
      if (!duplicateReported) {
        duplicateReported = true

        try {
          if (typeof onDuplicateStart === 'function') {
            onDuplicateStart()
          }
        } catch (error) {
          warnAboutTelemetryFailure(error)
        }
      }

      return undefined
    }

    started = true

    return start.apply(this, args)
  }

  Object.defineProperties(guardedStart, {
    [START_GUARD_MARKER]: { value: true },
    [ORIGINAL_START_MARKER]: { value: start }
  })
  Livewire.start = guardedStart

  return guardedStart
}
